import { Controller } from "@hotwired/stimulus"

// The page's AI on a creator's page (30 Oct 2026, Creators::AgentsController,
// ported from Faves' shop.js). The conversation lives in this browser only:
// sessionStorage keeps the current one, and the whole of it (ten messages)
// goes with each question, with the places each answer showed so the next
// turn knows them. An answer is drawn from JSON — text as text, never HTML —
// as a bubble, the stories of the first place, a card per place (its photo,
// a small map, "See it" opening the place's sheet), her links, and the
// buttons to tap next.
export default class extends Controller {
  static targets = ["chat", "log", "input", "mic", "send", "suggestions", "menu", "filter", "menuList", "attached", "history"]
  static values = { url: String, handle: String, greeting: String, avatar: String, places: Array }

  connect() {
    // This browser's conversations with her AI (Faves § 7.3): twenty per
    // page, thirty messages each, in localStorage; the one in progress named
    // in sessionStorage, so a reload or a visit to a place comes back to it.
    this.listKey = `spots.chats.${this.handleValue}`
    this.currentKey = `spots.chat.${this.handleValue}`
    let id = null
    try { id = sessionStorage.getItem(this.currentKey) } catch (_) {}
    const found = id && this.chats().find((c) => c.id === id)
    this.id = found ? id : this.newId()
    this.messages = found ? found.messages : []
    this.busy = false
    this.Recognition = window.SpeechRecognition || window.webkitSpeechRecognition
    this.onAsk = (event) => {
      const el = event.target.closest("[data-agent-ask]")
      if (el) setTimeout(() => this.attach(el.dataset.agentAsk, el.dataset.agentAskName), 0)
    }
    document.addEventListener("click", this.onAsk)
    this.typed()
  }

  disconnect() {
    this.recognition?.abort()
    document.removeEventListener("click", this.onAsk)
    document.body.classList.remove("spa-open")
  }

  newId() { return Date.now().toString(36) + Math.random().toString(36).slice(2, 6) }

  chats() {
    try { return JSON.parse(localStorage.getItem(this.listKey) || "[]") } catch (_) { return [] }
  }

  save() {
    const messages = this.messages.slice(-30)
    const first = messages.find((m) => m.role === "user")
    const others = this.chats().filter((c) => c.id !== this.id)
    const list = first ? [{ id: this.id, title: (first.about ? `${first.about}: ` : "") + first.text, at: Date.now(), messages }, ...others] : others
    try {
      localStorage.setItem(this.listKey, JSON.stringify(list.slice(0, 20)))
      sessionStorage.setItem(this.currentKey, this.id)
    } catch (_) {}
  }

  toggleHistory() {
    const menu = this.historyTarget
    menu.hidden = !menu.hidden
    if (menu.hidden) return
    const items = this.chats().slice(0, 5).map((c) => {
      const b = Object.assign(document.createElement("button"), { type: "button", className: c.id === this.id ? "on" : "" })
      const when = document.createElement("small")
      when.textContent = new Date(c.at).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })
      b.append(document.createTextNode(c.title.slice(0, 60)), when)
      b.addEventListener("click", () => { this.id = c.id; this.messages = c.messages; menu.hidden = true; this.save(); this.render() })
      return b
    })
    const fresh = Object.assign(document.createElement("button"), { type: "button", textContent: "＋ New chat" })
    fresh.addEventListener("click", () => { menu.hidden = true; this.reset() })
    menu.replaceChildren(...items, fresh)
  }

  typed() {
    const el = this.inputTarget
    el.style.height = "auto"
    el.style.height = Math.min(el.scrollHeight, 96) + "px"
    const empty = el.value.trim() === ""
    const canDictate = !!this.Recognition
    this.micTarget.hidden = !(empty && canDictate)
    this.sendTarget.hidden = empty && canDictate
  }

  keydown(event) {
    if (event.key === "Enter" && !event.shiftKey && !event.isComposing) {
      event.preventDefault()
      this.submit(event)
    }
  }

  submit(event) {
    event?.preventDefault()
    const text = this.inputTarget.value.trim()
    if (!text && !this.attachment) return
    this.inputTarget.value = ""
    this.typed()
    this.ask(text || "Tell me about this place")
  }

  suggest(event) { this.ask(event.currentTarget.textContent.trim()) }

  open() {
    if (!this.chatTarget.hidden) return
    this.chatTarget.hidden = false
    document.body.classList.add("spa-open")
    this.render()
  }

  close() {
    this.chatTarget.hidden = true
    document.body.classList.remove("spa-open")
  }

  reset() {
    this.id = this.newId()
    this.messages = []
    try { sessionStorage.setItem(this.currentKey, this.id) } catch (_) {}
    this.render()
  }

  // The whole conversation drawn again: the greeting, then each message.
  render() {
    this.logTarget.replaceChildren(this.bubble("assistant", this.greetingValue))
    this.messages.forEach((m, i) => {
      if (m.role === "user") this.logTarget.append(this.bubble("user", m.text, m.about))
      else this.logTarget.append(...this.answerNodes(m.answer || { text: m.text }, i === this.messages.length - 1))
    })
  }

  async ask(text) {
    if (this.busy) return
    this.busy = true
    this.open()
    const attach = this.attachment
    this.clearAttach()
    this.messages.push({ role: "user", text, attach: attach && { kind: "place", id: attach.id }, about: attach?.name })
    this.save()
    this.logTarget.append(this.bubble("user", text, attach?.name))
    this.logTarget.querySelectorAll(".spa-replies").forEach((r) => r.classList.add("answered"))
    const dots = this.row("assistant")
    dots.append(Object.assign(document.createElement("span"), { className: "spa-dots", innerHTML: "<i></i><i></i><i></i>" }))
    this.logTarget.append(dots)
    dots.scrollIntoView({ block: "end", behavior: "smooth" })

    let answer
    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          messages: this.messages.slice(-10).map((m) => ({ role: m.role, text: m.text, attach: m.attach, shown: m.answer?.places?.map((p) => p.id) })),
          lang: navigator.language,
        }),
      })
      answer = await response.json()
    } catch (_) {
      answer = { text: "Something went wrong on my side. Try me again in a moment.", places: [], links: [], replies: [] }
    }
    dots.remove()
    this.messages.push({ role: "assistant", text: answer.text || "", answer })
    this.save()
    const nodes = this.answerNodes(answer, true)
    this.logTarget.append(...nodes)
    nodes[0].scrollIntoView({ block: "start", behavior: "smooth" })
    this.busy = false
  }

  row(role) {
    const row = document.createElement("div")
    row.className = role === "user" ? "spa-row me" : "spa-row"
    if (role !== "user") {
      const av = document.createElement("img")
      av.className = "spa-av"; av.src = this.avatarValue; av.alt = ""
      row.append(av)
    }
    return row
  }

  bubble(role, text, about) {
    const row = this.row(role)
    const bub = document.createElement("span")
    bub.className = "spa-bub"
    if (about) {
      const tag = document.createElement("small")
      tag.className = "spa-about"
      tag.textContent = `📍 ${about}`
      bub.append(tag)
    }
    bub.append(document.createTextNode(text))
    row.append(bub)
    return row
  }

  answerNodes(answer, live) {
    const nodes = [this.bubble("assistant", answer.text || "")]
    const more = document.createElement("div")
    more.className = "spa-more"
    const places = answer.places || []
    const first = places[0]
    if (first?.stories?.length) {
      const strip = document.createElement("div")
      strip.className = "spa-stories"
      first.stories.forEach((s, i) => {
        const b = document.createElement("button")
        b.type = "button"
        b.addEventListener("click", () => this.see(first.id))
        const img = Object.assign(document.createElement("img"), { src: s.image, alt: "", loading: "lazy" })
        const tag = document.createElement("em")
        tag.textContent = i === 0 ? [s.highlight, s.date].filter(Boolean).join(" · ") : (s.date || "")
        b.append(img, tag)
        strip.append(b)
      })
      more.append(strip)
    }
    places.forEach((p, i) => more.append(this.card(p, i === 0)))
    if (answer.links?.length) {
      const links = document.createElement("div")
      links.className = "spa-links"
      answer.links.forEach((l) => {
        const a = Object.assign(document.createElement("a"), { href: l.url, target: "_blank", rel: "noopener" })
        a.textContent = l.title
        links.append(a)
      })
      more.append(links)
    }
    if (answer.replies?.length) {
      const replies = document.createElement("div")
      replies.className = live ? "spa-replies" : "spa-replies answered"
      answer.replies.forEach((r) => {
        const b = Object.assign(document.createElement("button"), { type: "button" })
        b.textContent = r
        b.addEventListener("click", () => {
          if (replies.classList.contains("answered") || this.busy) return
          b.classList.add("picked")
          this.ask(r)
        })
        replies.append(b)
      })
      more.append(replies)
    }
    if (more.childElementCount) nodes.push(more)
    return nodes
  }

  card(p, withMap) {
    const card = document.createElement("div")
    card.className = "spa-card"
    if (withMap && p.lat && p.lng) {
      const map = document.createElement("div")
      map.className = "spa-map"
      map.dataset.controller = "place-map"
      map.dataset.placeMapLatValue = p.lat
      map.dataset.placeMapLngValue = p.lng
      card.append(map)
    }
    const info = document.createElement("div")
    info.className = "spa-info"
    if (p.image) info.append(Object.assign(document.createElement("img"), { src: p.image, alt: "", loading: "lazy" }))
    const text = document.createElement("span")
    const name = document.createElement("b")
    name.textContent = p.name
    const sub = document.createElement("small")
    sub.textContent = [p.type, p.area, p.rating ? `${p.rating.toFixed(1)}★` : null].filter(Boolean).join(" · ")
    text.append(name, sub)
    if (p.filmed) {
      const when = document.createElement("small")
      when.textContent = `Filmed ${p.filmed}`
      text.append(when)
    }
    const open = Object.assign(document.createElement("button"), { type: "button", className: "spa-open" })
    open.textContent = "See it"
    open.addEventListener("click", () => this.see(p.id))
    info.append(text, open)
    card.append(info)
    return card
  }

  // The paperclip: one of her places, joined to the next question
  // (Faves' attachments, § 7.4). The list is the page's own places.
  toggleMenu() {
    this.menuTarget.hidden = !this.menuTarget.hidden
    if (!this.menuTarget.hidden) { this.filterTarget.value = ""; this.filterPlaces(); this.filterTarget.focus() }
  }

  filterPlaces() {
    const q = this.filterTarget.value.trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    const fold = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    const list = this.placesValue.filter(([, name, sub]) => !q || fold(`${name} ${sub}`).includes(q)).slice(0, 30)
    this.menuListTarget.replaceChildren(...list.map(([id, name, sub]) => {
      const b = Object.assign(document.createElement("button"), { type: "button" })
      const small = document.createElement("small")
      small.textContent = sub
      b.append(document.createTextNode(name), small)
      b.addEventListener("click", () => this.attach(id, name))
      return b
    }))
  }

  attach(id, name) {
    this.attachment = { id, name }
    this.menuTarget.hidden = true
    const chip = document.createElement("span")
    chip.className = "spa-chip"
    chip.append(document.createTextNode(`📍 ${name}`))
    const x = Object.assign(document.createElement("button"), { type: "button", textContent: "×" })
    x.setAttribute("aria-label", "Remove")
    x.addEventListener("click", () => this.clearAttach())
    chip.append(x)
    this.attachedTarget.replaceChildren(chip)
    this.attachedTarget.hidden = false
    this.inputTarget.focus()
  }

  clearAttach() {
    this.attachment = null
    this.attachedTarget.hidden = true
    this.attachedTarget.replaceChildren()
  }

  // The place's own sheet, as its row on the page opens it.
  see(id) {
    const modal = document.querySelector(`[data-partner-modal-id="${CSS.escape(id)}"]`)
    if (!modal) return
    this.close()
    this.application.getControllerForElementAndIdentifier(modal, "partner-modal")?.open()
  }

  // Dictation only, as on Faves: the browser's speech recognition fills the
  // field, and the question leaves when the speaker stops.
  dictate() {
    if (!this.Recognition) return
    if (this.recognition) { this.recognition.stop(); return }
    const rec = new this.Recognition()
    rec.lang = navigator.language || "en-US"
    rec.interimResults = true
    rec.onresult = (e) => {
      this.inputTarget.value = Array.from(e.results).map((r) => r[0].transcript).join("")
      this.typed()
      this.micTarget.hidden = false
    }
    rec.onend = () => {
      this.micTarget.classList.remove("listening")
      this.recognition = null
      if (this.inputTarget.value.trim()) this.submit()
      else this.typed()
    }
    this.recognition = rec
    this.micTarget.classList.add("listening")
    rec.start()
  }
}
